import { useLayoutEffect, useRef, useState } from 'react';
import { CapturedNode } from './SavedSlide.jsx';
import GoalSheetView from './GoalSheetView.jsx';
import NoticedView from './NoticedView.jsx';

const WIDTH = 980;
const field = (value, state) => ({ state: state || (Number.isFinite(value) ? 'known' : 'missing'), value });
function savedFacts(b) {
  return {
    name: b.name, period: b.period,
    status: { state: b.status ? 'known' : 'missing', raw: b.status },
    spending: field(b.spending, b.spendingState),
    pools: Object.fromEntries(Object.entries(b.pools || {}).map(([k,v]) => [k,field(v)])),
    unclassified: field(b.unclassified),
    income: { ...field(b.income), complete: b.incomeComplete,
      rows: (b.incomeRows || []).map(([label,value,kind],i) => ({key:i,label,kind:kind||null,amount:field(value)})) },
  };
}

// Match the same saved text, never today's household values. Text ranges give
// real glyph bounds rather than a full-width heading's CSS box. Repeated labels
// are matched in document order (for example two Employment rows).
function anchors(root) {
  const base = root.getBoundingClientRect();
  const found = new Map();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    const text = node.textContent.trim().replace(/\s+/g, ' ');
    const key = ['Your portfolio, under the surface', 'What the statements you supplied hold'].includes(text) ? 'Your current portfolio' : text;
    if (!key || !node.parentElement.getClientRects().length) continue;
    const range = document.createRange(); range.selectNodeContents(node);
    const r = range.getBoundingClientRect();
    if (!r.width || !r.height) continue;
    const list = found.get(key) || [];
    list.push({ x:r.x-base.x,y:r.y-base.y,w:r.width,h:r.height,
      font:parseFloat(getComputedStyle(node.parentElement).fontSize) });
    found.set(key,list);
  }
  return found;
}
function distance(p,r) {
  return Math.hypot(Math.max(r.x-p[0],0,p[0]-r.x-r.w), Math.max(r.y-p[1],0,p[1]-r.y-r.h));
}

// Preserve each mark's shape. A local mark moves with its matching saved text
// and scales uniformly with its type; the original capture remains untouched
// in the illustration drawer. Broad sketches stay with their original canvas.
export function placeReportInk(strokes, width, before, after) {
  const pairs = [];
  for (const [key,list] of before) list.forEach((a,i) => {
    const b=after.get(key)?.[i]; if(b) pairs.push({a,b,key});
  });
  if (!pairs.length) return null;
  const output=[];
  for (const stroke of strokes) {
    const pts=(stroke.p || []).filter(p=>p.length===2 && p.every(Number.isFinite)).map(([x,y])=>[x*width,y*width]);
    if (!pts.length) continue;
    const xs=pts.map(p=>p[0]),ys=pts.map(p=>p[1]);
    const center=[(Math.min(...xs)+Math.max(...xs))/2,(Math.min(...ys)+Math.max(...ys))/2];
    const pair=pairs.reduce((best,p)=>!best || distance(center,p.a)<distance(center,best.a)?p:best,null);
    // A shape covering distant subjects cannot honestly be reassigned to one.
    // Keep the original layout in that case instead of guessing its meaning.
    if (distance(center,pair.a)>100 || Math.max(...xs)-Math.min(...xs)>Math.max(500,pair.a.w*1.7) || Math.max(...ys)-Math.min(...ys)>220) return null;
    const underline=(Math.max(...xs)-Math.min(...xs))>4*(Math.max(...ys)-Math.min(...ys)) && center[1]>=pair.a.y+pair.a.h*.75;
    const scale=underline?Math.min(pair.b.font/pair.a.font,pair.b.w/pair.a.w):pair.b.font/pair.a.font;
    const oldY=underline?pair.a.y+pair.a.h:pair.a.y+pair.a.h/2;
    const newY=underline?pair.b.y+pair.b.h:pair.b.y+pair.b.h/2;
    output.push({...stroke, w:(stroke.w||3)*scale,
      points:pts.map(([x,y])=>[pair.b.x+(x-pair.a.x)*scale,newY+(y-oldY)*scale])});
  }
  return output;
}

export default function ReportIllustration({page, fallback}) {
  const original=useRef(null), report=useRef(null);
  const [layout,setLayout]=useState(null);
  useLayoutEffect(()=>{
    let cancelled=false;
    const measure=()=>{
      if(cancelled || !report.current || (page.capture && !original.current)) return;
      const ink=page.capture ? placeReportInk(page.strokes,page.capture.width,anchors(original.current),anchors(report.current)) : [];
      let x=0,y=0,right=WIDTH,bottom=report.current.scrollHeight;
      for(const s of ink || []) for(const [px,py] of s.points){x=Math.min(x,px-5);y=Math.min(y,py-5);right=Math.max(right,px+5);bottom=Math.max(bottom,py+5);}
      setLayout({ink,box:[x,y,right-x,bottom-y]});
    };
    measure(); document.fonts.ready.then(measure);
    return ()=>{cancelled=true;};
  },[page]);
  const facts=savedFacts(page.basis);
  const kind=page.step==='goals'?'goals':page.board || 'tax';
  // `report` renders the figures as printed text: no inputs, no breakdowns, no
  // observations, because this page is a picture of the figures the drawing
  // was made over and the anchors below match on that text.
  const content=page.step==='goals'
    ? <GoalSheetView report facts={facts} comparison={page.basis.comparison || {ready:false}}/>
    : <NoticedView report facts={facts} comparison={{...page.basis.comparison,income:page.basis.income,spending:page.basis.spending}} board={page.board} analysis={page.basis.analysis} onBoard={()=>{}}/>;
  const label=page.title?.replace(/goal[ -]sheet/gi, 'Goals') || (kind==='portfolio'?'Portfolio analysis':kind==='goals'?'Goals':'Tax picture');
  const body=<div className={`conv-report-content is-${kind}`} ref={report}>
    {content}
  </div>;
  return <>
    <div className="conv-report-measure" aria-hidden="true">
      {page.capture && <div ref={original} className="conv-captured-root" style={{width:page.capture.width,height:page.capture.height}}>
        <CapturedNode node={page.capture.tree} styles={page.capture.styles}/>
      </div>}
      {body}
    </div>
    {layout?.ink ? <svg className={page.capture ? "conv-saved-slide conv-report-slide" : "conv-report-page"} viewBox={layout.box.join(' ')} preserveAspectRatio="xMidYMin meet" role="img" aria-label={page.capture ? `${label}, saved figures and annotations` : label}>
      <foreignObject x="0" y="0" width={WIDTH} height={layout.box[3]}>
        <div xmlns="http://www.w3.org/1999/xhtml" className={`conv-report-content is-${kind}`}>
          {content}
        </div>
      </foreignObject>
      {layout.ink.map((s,i)=><polyline key={i} points={s.points.map(p=>p.join(',')).join(' ')} fill="none" stroke={s.c} strokeWidth={s.w} strokeLinecap="round" strokeLinejoin="round"/>)}
    </svg>:fallback}
  </>;
}
